import {
  cloneElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type TouchEvent as ReactTouchEvent,
} from 'react';
import { createPortal } from 'react-dom';

const HOVER_DELAY = 300;
const LONG_PRESS = 500;
const ANCHOR_GAP = 6;
const EDGE = 8;
const ARROW = 8;

interface Props {
  /** The whole tooltip sentence. Null disables the tooltip entirely. */
  content: string | null;
  children: ReactElement;
}

/**
 * The rail's tooltip. It exists because every action in the bar is a bare
 * icon, so the name has to arrive on hover and on keyboard focus.
 *
 * No `aria-describedby`: the consumer already sets `aria-label` to this same
 * sentence, and a described-by would make a screen reader read it twice.
 * The child keeps its own handlers; we only add ours alongside.
 */
export default function Tooltip({ content, children }: Props) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  // The bar's bottom edge when the anchor sits in the header: a 28px icon in
  // a 52px row ends 7px above the bar, and the tip must clear the bar itself.
  const [floor, setFloor] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number; arrow: number } | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    clearTimer();
    setRect(null);
    setPos(null);
  }, [clearTimer]);

  const show = useCallback((fromTouch = false) => {
    const anchor = anchorRef.current;
    if (!anchor || !content) return;
    if (fromTouch) longPressFiredRef.current = true;
    setFloor(anchor.closest('header')?.getBoundingClientRect().bottom ?? 0);
    setRect(anchor.getBoundingClientRect());
  }, [content]);

  const showAfter = useCallback(
    (delay: number, fromTouch = false) => {
      clearTimer();
      timerRef.current = window.setTimeout(() => show(fromTouch), delay);
    },
    [clearTimer, show],
  );

  useEffect(() => clearTimer, [clearTimer]);

  // Content can vanish mid-hover (a button that stops being disabled, say).
  useEffect(() => {
    if (!content) hide();
  }, [content, hide]);

  // Measure once mounted so the tip can be centred on the anchor and kept
  // inside the viewport. It sits below the bar, never over it.
  useLayoutEffect(() => {
    if (!rect) return;
    const width = tipRef.current?.offsetWidth ?? 0;
    const centre = rect.left + rect.width / 2;
    const left = Math.max(EDGE, Math.min(centre - width / 2, window.innerWidth - width - EDGE));
    setPos({
      top: Math.max(rect.bottom, floor) + ANCHOR_GAP,
      left,
      arrow: Math.max(ARROW, Math.min(centre - left - ARROW / 2, Math.max(ARROW, width - ARROW * 2))),
    });
  }, [rect, floor, content]);

  // Anything that moves the anchor or takes attention elsewhere kills it.
  useEffect(() => {
    if (!rect) return;
    function onPointerDown(e: MouseEvent) {
      if (anchorRef.current?.contains(e.target as Node)) return;
      hide();
    }
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [rect, hide]);

  function capture(e: { currentTarget: EventTarget & HTMLElement }) {
    anchorRef.current = e.currentTarget;
  }

  const child = children as ReactElement<Record<string, unknown>>;
  const props = child.props;

  function chain<E>(theirs: unknown, ours: (e: E) => void) {
    return (e: E) => {
      (theirs as ((e: E) => void) | undefined)?.(e);
      ours(e);
    };
  }

  const clone = cloneElement(child, {
    onMouseEnter: chain<ReactMouseEvent<HTMLElement>>(props.onMouseEnter, (e) => {
      capture(e);
      showAfter(HOVER_DELAY);
    }),
    // A pointer click hides the tip (it is about to act). Not on a dimmed
    // control, whose tip is the only place its reason lives; and not on a
    // keyboard activation (detail 0), where focus and the tip stay put.
    onMouseDown: chain<ReactMouseEvent<HTMLElement>>(props.onMouseDown, (e) => {
      if (e.currentTarget.getAttribute('aria-disabled') !== 'true') hide();
    }),
    onClick: chain<ReactMouseEvent<HTMLElement>>(props.onClick, (e) => {
      if (e.detail > 0 && e.currentTarget.getAttribute('aria-disabled') !== 'true') hide();
    }),
    onMouseLeave: chain<ReactMouseEvent<HTMLElement>>(props.onMouseLeave, hide),
    onFocus: chain<ReactFocusEvent<HTMLElement>>(props.onFocus, (e) => {
      capture(e);
      // Only keyboard focus earns a tooltip. A mouse click already focused
      // the button, and popping the tip open under the cursor is noise.
      let keyboard = false;
      try {
        keyboard = e.currentTarget.matches(':focus-visible');
      } catch {
        // Safari < 15.4 has no :focus-visible; treat focus as keyboard.
        keyboard = true;
      }
      if (keyboard) show();
    }),
    onBlur: chain<ReactFocusEvent<HTMLElement>>(props.onBlur, hide),
    onKeyDown: chain<ReactKeyboardEvent<HTMLElement>>(props.onKeyDown, (e) => {
      if (e.key === 'Escape') hide();
    }),
    onTouchStart: chain<ReactTouchEvent<HTMLElement>>(props.onTouchStart, (e) => {
      capture(e);
      longPressFiredRef.current = false;
      showAfter(LONG_PRESS, true);
    }),
    onTouchEnd: chain<ReactTouchEvent<HTMLElement>>(props.onTouchEnd, (e) => {
      if (longPressFiredRef.current) e.preventDefault();
      longPressFiredRef.current = false;
      hide();
    }),
    onTouchCancel: chain<ReactTouchEvent<HTMLElement>>(props.onTouchCancel, () => {
      longPressFiredRef.current = false;
      hide();
    }),
    onTouchMove: chain<ReactTouchEvent<HTMLElement>>(props.onTouchMove, hide),
  });

  return (
    <>
      {clone}
      {content &&
        rect &&
        createPortal(
          <div
            ref={tipRef}
            role="tooltip"
            aria-hidden="true"
            className="
              fixed z-[70] pointer-events-none
              bg-fg text-page text-[11.5px] leading-[1.35]
              px-2 py-1.5 rounded-[6px] shadow-flare max-w-[min(320px,calc(100vw-16px))] whitespace-normal text-left
              animate-[fade-in_120ms_var(--ease-out-expo)_both] motion-reduce:animate-none
            "
            style={{ top: pos?.top ?? Math.max(rect.bottom, floor) + ANCHOR_GAP, left: pos?.left ?? 0 }}
          >
            <span
              aria-hidden
              className="absolute w-2 h-2 bg-fg rotate-45"
              style={{ top: -ARROW / 2, left: pos?.arrow ?? 0 }}
            />
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
